import { z } from 'zod';
import { PersonRef } from './people.js';
/**
 * Chapi and the work (docs/adr/0063-chapi-task-breakdown-and-updates.md; BRIEF_CHANGE_PROPOSALS 3.12, approved by
 * Nathan 18 Oct 2026). Three helpers, all human in the loop: a brief becomes a proposed task breakdown that a person
 * edits and confirms; Chapi notices when a task looks done and asks its assignee; and Chapi drafts a client progress
 * update that a person reviews and sends. Nothing here creates, changes or sends anything without that person.
 */
// --- Brief → task breakdown -----------------------------------------------------------------------------------
/** Longest brief Chapi reads, pasted or from a file's extracted text. Longer text is cut (and the person told). */
export const BREAKDOWN_MAX_BRIEF_CHARS = 60_000;
/** At most this many tasks in a breakdown, and subtasks per task. */
export const BREAKDOWN_MAX_TASKS = 60;
export const BREAKDOWN_MAX_SUBTASKS = 20;
export const BreakdownTask = z.object({
    title: z.string().trim().min(1).max(200),
    description: z.string().max(4000).default(''),
    subtasks: z.array(z.string().trim().min(1).max(200)).max(BREAKDOWN_MAX_SUBTASKS).default([]),
    /** The team's estimate, in minutes; null for none. */
    estimateMinutes: z.number().int().min(0).max(100_000).nullable().default(null),
    assigneeUserId: z.uuid().nullable().default(null),
    /** A phase or grouping ("Design", "Build"), in the suggested order; empty for none. Becomes a label. */
    group: z.string().trim().max(32).default(''),
});
export const BreakdownSource = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('text'), text: z.string().trim().min(20, 'Paste a little more of the brief').max(BREAKDOWN_MAX_BRIEF_CHARS) }),
    z.object({ kind: z.literal('file'), fileId: z.uuid() }),
]);
export const CreateTaskBreakdownInput = z.object({ projectId: z.uuid(), source: BreakdownSource });
export const TaskBreakdownStatus = z.enum(['drafting', 'ready', 'failed', 'confirmed']);
/** A proposed breakdown. Only the person who asked sees it; nothing exists as work until they confirm. */
export const TaskBreakdown = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    projectName: z.string(),
    status: TaskBreakdownStatus,
    source: z.object({ kind: z.enum(['text', 'file']), fileName: z.string().nullable(), truncated: z.boolean() }),
    items: z.array(BreakdownTask),
    /** Chapi's one-line note about what it proposed or guessed. */
    note: z.string(),
    error: z.string().nullable(),
    /** The tasks made when it was confirmed. */
    created: z.array(z.object({ id: z.uuid(), key: z.string(), title: z.string() })),
    createdAt: z.string(),
});
export const ConfirmTaskBreakdownInput = z.object({ items: z.array(BreakdownTask).min(1).max(BREAKDOWN_MAX_TASKS) });
/** A file Chapi could read a brief from: one the person can see in this project's channels or tasks. */
export const BriefFile = z.object({
    id: z.uuid(),
    name: z.string(),
    sizeBytes: z.number().int(),
    /** `ready`: its text has been read; `pending`: not yet (Chapi reads it when asked); otherwise why it can't be. */
    text: z.enum(['ready', 'pending', 'too_large', 'unsupported', 'failed']),
    createdAt: z.string(),
});
export const BriefFileList = z.object({ items: z.array(BriefFile) });
// --- "Looks done" ---------------------------------------------------------------------------------------------
export const DoneSignal = z.enum(['pull_request_merged', 'commit_closes', 'message', 'subtasks_done']);
/** Chapi thinks a task may be finished. Only its assignee sees it; one tap marks it done or dismisses it. */
export const DoneSuggestion = z.object({
    id: z.uuid(),
    workItem: z.object({ id: z.uuid(), key: z.string(), title: z.string(), projectName: z.string(), statusId: z.uuid() }),
    signal: DoneSignal,
    /** Why, in a few words: "Pull request #12 was merged", "All 4 subtasks are done". Never commit or message text. */
    reason: z.string(),
    createdAt: z.string(),
});
export const DoneSuggestionList = z.object({ items: z.array(DoneSuggestion) });
export const AcceptDoneSuggestionResponse = z.object({ workItemId: z.uuid(), statusId: z.uuid() });
/**
 * Whether a short chat message says the work is finished ("done", "all done ✅", "this is finished", "shipped it"),
 * and not "not done", "almost done" or a question. Rules only: Chapi never sends a message to a model for this.
 */
export function saysDone(text) {
    const t = text.toLowerCase().replace(/\s+/g, ' ').trim();
    if (!t || t.length > 280 || t.includes('?'))
        return false;
    if (/\b(not|isn'?t|isnt|wasn'?t|aren'?t|almost|nearly|half|partly|partially|mostly|until|once|when|if|before|un)\s*(yet\s+)?(quite\s+)?(done|finished|complete|completed|shipped|deployed|fixed|sorted)\b/.test(t))
        return false;
    if (/\b(not yet|still|wip|in progress|blocked|todo|to do|tomorrow|later|will|going to|gonna|should|need to|needs to|get|getting|hope|hopefully)\b/.test(t) || /'ll\b/.test(t))
        return false;
    const word = '(done|finished|complete|completed|shipped|deployed|fixed|sorted|wrapped up)';
    return (/(^|\s)(✅|✔️|☑️)\s*$/.test(text.trim()) ||
        new RegExp(`^(all |now |and )?${word}\\b`).test(t) ||
        new RegExp(`\\b(this|that|it|task|ticket|all|everything|[a-z][a-z0-9]{0,5}-\\d+)( is|'s| has been| have been| are)? (now |all |finally |just )?${word}\\b`).test(t) ||
        new RegExp(`\\b(i'?ve|we'?ve|i have|we have|just) (now |finally |just )?${word}\\b`).test(t) ||
        new RegExp(`\\b${word}[ .!🎉✅]*$`).test(t));
}
/**
 * Task keys a commit message says it finishes: "Fixes HPA-9", "closes WEB-12, WEB-13", "HPA-9 done". The keys are
 * the same shape the repositories module links (docs/adr/0052).
 */
export function closingTaskKeys(message) {
    const out = [];
    const text = message.slice(0, 2000);
    const KEY = '([A-Z][A-Z0-9]{0,5}-[1-9][0-9]{0,6})';
    const before = new RegExp(`\\b(?:fix(?:es|ed)?|close[sd]?|resolve[sd]?|complete[sd]?|finish(?:es|ed)?|done)\\s*:?\\s+((?:${KEY}(?:\\s*(?:,|and|&)\\s*)?)+)`, 'gi');
    for (const m of text.matchAll(before))
        for (const k of m[1].matchAll(new RegExp(KEY, 'gi')))
            if (!out.includes(k[1].toUpperCase()))
                out.push(k[1].toUpperCase());
    const after = new RegExp(`${KEY}\\s*(?:is\\s+)?(?:done|finished|complete|completed|fixed)\\b`, 'g');
    for (const m of text.matchAll(after))
        if (!out.includes(m[1]))
            out.push(m[1]);
    return out.slice(0, 20);
}
// --- Client progress updates ------------------------------------------------------------------------------------
export const ClientUpdateCadence = z.enum(['off', 'weekly', 'fortnightly']);
/** One task as it went into a draft: its title and dates only (never estimates, time, comments or rates). */
export const ClientUpdateFact = z.object({
    key: z.string(),
    title: z.string(),
    /** Shared with the client (client-visible); the others are shown to the reviewer marked "internal". */
    shared: z.boolean(),
    dueDate: z.string().nullable(),
});
/** Where a task stood in the period: done in it, in progress or review, blocked or waiting, or coming up. */
export const ClientUpdateState = z.enum(['done', 'in_progress', 'blocked', 'upcoming']);
/**
 * The lists the draft is written from contain only tasks shared with the client. Internal tasks that moved are kept
 * apart in `internal`: never sent to the model or put in the draft, shown to the reviewer as "Not included
 * (internal)", and only added to the text by the reviewer, one at a time, after a warning.
 */
export const ClientUpdateFacts = z.object({
    done: z.array(ClientUpdateFact),
    inProgress: z.array(ClientUpdateFact),
    blocked: z.array(ClientUpdateFact),
    upcoming: z.array(ClientUpdateFact),
    internal: z.array(ClientUpdateFact.extend({ state: ClientUpdateState })).default([]),
});
/** The line the reviewer adds for an internal task they choose to share: its title and state, never its key. */
export function internalFactLine(f) {
    const state = { done: 'done', in_progress: 'in progress', blocked: 'waiting on a decision', upcoming: 'coming up next' }[f.state];
    return `- ${f.title} (${state})`;
}
/**
 * Keys of internal tasks that appear in a text, as whole words ("WEB-12", not "WEB-123"). Sending an update whose
 * text still names one is refused (docs/adr/0063).
 */
export function internalKeysIn(text, keys) {
    return keys.filter((k) => new RegExp(`(^|[^A-Za-z0-9-])${k.replace(/[-]/g, '\\-')}(?![0-9A-Za-z])`, 'i').test(text));
}
export const ClientUpdateStatus = z.enum(['drafting', 'draft', 'failed', 'sent', 'discarded']);
export const ClientUpdateContact = z.object({ id: z.uuid(), name: z.string().nullable(), email: z.string(), emailLess: z.boolean() });
export const ClientUpdate = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    projectName: z.string(),
    clientId: z.uuid(),
    clientName: z.string(),
    status: ClientUpdateStatus,
    /** Drafted by the weekly or fortnightly schedule rather than by a person. */
    scheduled: z.boolean(),
    /** YYYY-MM-DD, end exclusive. */
    periodStart: z.string(),
    periodEnd: z.string(),
    subject: z.string(),
    body: z.string(),
    facts: ClientUpdateFacts,
    error: z.string().nullable(),
    createdAt: z.string(),
    sentAt: z.string().nullable(),
    sentBy: PersonRef.nullable(),
    recipients: z.array(z.object({ name: z.string().nullable(), email: z.string() })),
});
/** A project's updates tab: its cadence, who it can go to, and its drafts and sent updates (newest first). */
export const ClientUpdatesView = z.object({
    cadence: ClientUpdateCadence,
    /** Owners, admins and the project's team may draft and send; owners and admins set the cadence. */
    canSend: z.boolean(),
    canConfigure: z.boolean(),
    contacts: z.array(ClientUpdateContact),
    items: z.array(ClientUpdate),
});
export const PendingClientUpdates = z.object({ items: z.array(ClientUpdate) });
export const DraftClientUpdateInput = z.object({
    /** Days back from today the update covers (default 7). */
    days: z.number().int().min(1).max(31).default(7),
});
export const EditClientUpdateInput = z.object({
    subject: z.string().trim().min(1).max(200),
    body: z.string().trim().min(1).max(10_000),
});
export const SendClientUpdateInput = EditClientUpdateInput.extend({
    contactIds: z.array(z.uuid()).min(1, 'Choose at least one person').max(50),
});
//# sourceMappingURL=chapi-work.js.map